import { applyPatch, type Json, jsonEqual, validateOps, valueAt } from "../manifest/patch";
import { SDK_READ, SDK_WRITE } from "../manifest/readOnlyRpcs";
import { failed, ok, type ToolResult } from "../manifest/status";
import { inspectJsonSafety } from "../safety/jsonSafety";
import type { JsonObject, JsonValue } from "../wire/json";
import type { HostAdapter } from "../host/adapter";
import type { ToolHandler } from "./dispatcher";

/**
 * The tools every bridge-carrying frame answers, in ONE place.
 *
 * ## Why this file exists
 *
 * `bridge/dispatcher.ts` already says it: **ADVERTISED and IMPLEMENTED are
 * different sets.** A frame advertises its own `<app>__*` tools and implements
 * the fixed set declared in `manifest/`. Until 2026-09-14 each entry point
 * wrote its own copy of those implementations and the control app wrote none at
 * all — which is how the control frame came to register as an ordinary frame
 * that answers nothing, and shadow the whole fixed set whenever it happened to
 * be the most recently registered frame in the workspace.
 *
 * One copy, imported by every entry point, is what keeps "implements" and
 * "declares" the same statement.
 */

/** The one fixed host read, `giraffe_state` (`docs/tool-surface.md`). */
export function hostToolHandlers(host: HostAdapter): Record<string, ToolHandler> {
  return {
    giraffe_state: (args) => {
      const key = String(args.key);
      const read = host.getState(key);
      // Absent and empty are DIFFERENT ANSWERS and are reported differently:
      // the geo-typed keys return a well-formed EMPTY FeatureCollection for a
      // key that never arrived, so the value alone cannot tell them apart.
      return ok({ key, present: read.present, value: (read.value ?? null) as JsonValue });
    },
  };
}

/**
 * How long a patch waits for the host to show its write. Inside
 * `CALL_BUDGET_MS` (30 s), with room for both socket hops.
 */
const PATCH_CONFIRM_MS = 5_000;

type Confirmation = { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * `giraffe_sdk_patch`, in the frame: read, check, apply, write, confirm (#41).
 *
 * The relay has already refused anything malformed and resolved the target
 * into this plan, so there is no `PATCHABLE` here. `validateOps` runs again
 * because only it makes the `ValidOps` that `applyPatch` accepts; it is the
 * same function, not a copy of the rule.
 *
 * The whole read-modify-write is ONE frame call, so the relay's per-frame
 * queue lets nothing interleave, and the read-to-write gap is one JS turn.
 */
async function patchRecord(
  host: HostAdapter,
  args: JsonObject,
  confirmMs: number,
): Promise<ToolResult<JsonValue>> {
  const stateKey = String(args.stateKey);
  const id = String(args.id);
  const writeFn = String(args.writeFn);
  const checked = validateOps(args.ops);
  if (!checked.ok) return failed("invalid_arguments", `${checked.message}. Nothing was written.`);

  // 1. Read.
  const read = host.getState(stateKey);
  if (!read.present) {
    return failed(
      "soft_failure",
      `The host has not sent ${stateKey} to this app yet, so there is nothing to patch and ` +
        `nothing was written. Try again in a moment; if it persists, reload the Giraffe page.`,
    );
  }
  const records = read.value;
  if (!isPlainRecord(records) || !Object.hasOwn(records, id)) {
    const ids = isPlainRecord(records) ? Object.keys(records).join(", ") : "none";
    return failed(
      "invalid_arguments",
      `arguments.id: ${stateKey} has no record "${id}". It has: ${ids}. Nothing was written.`,
    );
  }
  const original = records[id];

  // 2. The host's write serialises the record, so a Map in it would silently empty.
  const safety = inspectJsonSafety(original, `${stateKey}["${id}"]`);
  if (!safety.ok) {
    return failed(
      "not_serializable",
      `${safety.violation.message} The host's write serialises the whole record, so this would ` +
        `be silently lost. Nothing was written.`,
    );
  }

  // 3. Apply, all or nothing. 4. Skip a write that changes nothing.
  const outcome = applyPatch(original as Json, checked.ops);
  if (!outcome.ok) return notWritten(outcome.conflict as unknown as JsonValue);
  if (outcome.changed.length === 0) return notWritten(null);

  // 5. Subscribe, THEN write, so a push that lands during the invoke is seen.
  const confirmation = confirm(
    host,
    stateKey,
    id,
    original as Json,
    outcome.record,
    outcome.changed,
    writeFn,
    confirmMs,
  );
  try {
    await host.invoke(writeFn, [id, outcome.record]);
    confirmation.arm();
  } catch (error) {
    confirmation.cancel();
    // The dispatcher reports this as host_rejected, sealed-fields caveat included.
    throw error;
  }

  // 6. Confirm.
  const seen = await confirmation.result;
  if (!seen.ok) return failed("soft_failure", seen.message);
  return ok({
    written: true,
    confirmed: "host_state",
    changed: [...outcome.changed],
    conflict: null,
    undo: outcome.undo as unknown as JsonValue,
  } as never);
}

function notWritten(conflict: JsonValue): ToolResult<JsonValue> {
  return ok({ written: false, confirmed: null, changed: [], conflict, undo: [] } as never);
}

/**
 * Wait for a push in which every changed path holds the patched value. A push
 * that changes something else is ignored. On timeout, the message tells apart
 * "nothing changed" (the host's silent no-edit-rights return) from "something
 * else is there now" (a concurrent edit).
 *
 * Subscribing and timing are separate on purpose: the subscription is made
 * before the write, so a push that lands during it is seen, and `arm` starts the
 * window only once the write has returned, so a slow write does not eat it.
 */
function confirm(
  host: HostAdapter,
  stateKey: string,
  id: string,
  original: Json,
  patched: Json,
  paths: readonly string[],
  writeFn: string,
  confirmMs: number,
): {
  readonly result: Promise<Confirmation>;
  readonly arm: () => void;
  readonly cancel: () => void;
} {
  let arm = (): void => undefined;
  let cancel = (): void => undefined;
  const result = new Promise<Confirmation>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const current = (): Json | null => {
      const records = host.getState(stateKey).value;
      return isPlainRecord(records) && Object.hasOwn(records, id) ? (records[id] as Json) : null;
    };
    const finish = (value: Confirmation): void => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      unsubscribe();
      resolve(value);
    };
    const unsubscribe = host.onStateChange([stateKey], () => {
      const now = current();
      if (now !== null && paths.every((path) => samePath(now, patched, path))) finish({ ok: true });
    });
    const expire = (): void => {
      const now = current();
      const untouched = now !== null && paths.every((path) => samePath(now, original, path));
      if (untouched) {
        finish({
          ok: false,
          message:
            `The host accepted ${writeFn} but ${stateKey}["${id}"] did not change within ` +
            `${confirmMs} ms. The host ignores this write without an error when the user cannot ` +
            `edit the project, so that is the likely cause: check the user can edit this project ` +
            `in Giraffe. Read the record with giraffe_state before trying again.`,
        });
        return;
      }
      const actual = Object.fromEntries(
        paths.map((path) => {
          const found = now === null ? { present: false as const } : valueAt(now, path);
          return [path, found.present ? found.value : null];
        }),
      );
      finish({
        ok: false,
        message:
          `The host accepted ${writeFn}, but ${stateKey}["${id}"] now holds different values at ` +
          `the patched paths: ${JSON.stringify(actual)}. Someone else probably changed the same ` +
          `fields at the same moment. Read the record again with giraffe_state and decide whether ` +
          `to patch again.`,
      });
    };
    arm = () => {
      if (!settled && timer === null) timer = setTimeout(expire, confirmMs);
    };
    cancel = () => finish({ ok: false, message: "cancelled" });
  });
  return { result, arm, cancel };
}

/**
 * The host's push strips every `null` at any depth (`cloneDeepWithoutNull`), so
 * a field the patch set to null arrives absent. Both sides are compared with
 * nulls stripped, and null and absent count as the same value; without this a
 * write that landed would read as someone else's edit.
 */
function samePath(a: Json, b: Json, path: string): boolean {
  const left = valueAt(a, path);
  const right = valueAt(b, path);
  const leftValue = left.present ? withoutNulls(left.value) : null;
  const rightValue = right.present ? withoutNulls(right.value) : null;
  if (leftValue === null || rightValue === null) return leftValue === rightValue;
  return jsonEqual(leftValue, rightValue);
}

/** Object keys holding null removed, recursively; array elements kept in place. */
function withoutNulls(value: Json): Json {
  if (Array.isArray(value)) return value.map(withoutNulls);
  if (typeof value !== "object" || value === null) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, field]) => field !== null)
      .map(([key, field]) => [key, withoutNulls(field)]),
  );
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The generic SDK tools: read, write and patch. **Declared and implemented by the control frame only.**
 *
 * ## The checks are the relay's, and they are not repeated here
 *
 * A name refused by policy, a name that is not registered, a procedure sent to
 * the wrong SDK tool, and an argument list that cannot match the declaration
 * are all NAMED before a call is routed to
 * any frame — by `refuseSdkCall` in `relay/sdkTools.ts`. That is still the
 * whole argument for why a passthrough is defensible at all (see
 * `manifest/tools/sdk.ts`); it just no longer happens here.
 *
 * ⚠️ **Do not restore a copy of those checks.** `hostRpcSignatures.ts` is
 * GENERATED and labelled NOT A CONTRACT. A copy compiled into an app bundle
 * goes stale on that bundle's own schedule, and a stale copy then refuses a
 * call the relay allowed while saying *"that procedure is not registered"* — a
 * sentence about the host, when the truth is about the bundle. One copy, at
 * the relay, is what keeps that sentence true.
 *
 * What is left is the frame's own job, which no relay can do: reach the host,
 * and normalise what comes back.
 */
export function sdkToolHandlers(
  host: HostAdapter,
  options: { readonly patchConfirmMs?: number } = {},
): Record<string, ToolHandler> {
  const confirmMs = options.patchConfirmMs ?? PATCH_CONFIRM_MS;
  const invoke: ToolHandler = async (args) => {
    const fn = String(args.fn);
    const callArgs: readonly JsonValue[] = Array.isArray(args.args) ? args.args : [];
    const answer = await host.invoke(fn, callArgs);
    // `?? null` because a host procedure that returns nothing resolves
    // `undefined`, which `safety/` rejects — `flyTo` is the precedent.
    return ok({ fn, value: (answer ?? null) as JsonValue });
  };
  // One handler under both names: the RELAY decides which tool runs a procedure
  // (`relay/sdkTools.ts`'s `refuseSdkCall`), for the same reason it owns the arity check.
  return {
    [SDK_READ]: invoke,
    [SDK_WRITE]: invoke,
    giraffe_sdk_patch: (args) => patchRecord(host, args, confirmMs),
  };
}
